import { useMemo } from 'react';
import { OwnershipOption, UseOwnershipOptionsProps } from './useOwnershipOptions.props';

export const useOwnershipOptions = ({
  companyInfo,
  fundList,
  cashFlows = [],
}: UseOwnershipOptionsProps): OwnershipOption[] =>
  useMemo(() => {
    const options: OwnershipOption[] = [];

    // Funds referenced by any cash flow row for this cap table. Virtual rows
    // come from current FundOwnershipDetails, but saved rows also carry an
    // owner_fund for funds that have fully exited (no FOD row left) - those
    // must stay selectable or their Owner cell renders blank.
    const fundsWithOwnership = new Set<number>();
    cashFlows.forEach(item => {
      if (item.owner_fund) {
        fundsWithOwnership.add(item.owner_fund);
      }
    });

    // Only add funds that appear on this cap table's cash flows
    if (Array.isArray(fundList)) {
      const fundOptions = fundList
        .filter(fund => fund.id !== undefined && fundsWithOwnership.has(fund.id))
        .map(fund => ({
          id: `fund-${fund.id}`,
          value: `fund-${fund.id}`,
          label: fund.name ?? '',
          type: 'fund' as const,
        }));
      options.push(...fundOptions);
    }

    // Add child companies from companyInfo
    if (companyInfo?.child_companies) {
      const childCompanyOptions = companyInfo.child_companies.map((child: any) => ({
        id: `company-${child.company_id}`,
        value: `company-${child.company_id}`,
        label: child.company_name,
        type: 'company' as const,
      }));
      options.push(...childCompanyOptions);
    }

    return options;
  }, [companyInfo, fundList, cashFlows]);
